import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const SiteSettingsContext = createContext({ settings: null, refresh: () => {} });

const FONT_URLS = {
  Inter: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap",
  "Space Grotesk": "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap",
  Poppins: "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap",
  Manrope: "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap",
  "DM Sans": "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap",
  Nunito: "https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800&display=swap",
  "Plus Jakarta Sans": "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap",
};

export const AVAILABLE_FONTS = Object.keys(FONT_URLS);

function ensureFontLoaded(family) {
  if (!family || !FONT_URLS[family]) return;
  const id = `dyn-font-${family.replace(/\s+/g, "-")}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = FONT_URLS[family];
  document.head.appendChild(link);
}

export function applySiteSettings(settings) {
  if (!settings) return;
  const root = document.documentElement;
  if (settings.font_family) {
    ensureFontLoaded(settings.font_family);
    root.style.setProperty("--font-body", `"${settings.font_family}", ui-sans-serif, system-ui, sans-serif`);
  }
  if (settings.brand_primary_color) root.style.setProperty("--brand-primary", settings.brand_primary_color);
  if (settings.brand_secondary_color) root.style.setProperty("--brand-secondary", settings.brand_secondary_color);
  if (settings.brand_accent_color) root.style.setProperty("--brand-accent", settings.brand_accent_color);
  if (settings.brand_ink_color) root.style.setProperty("--brand-ink", settings.brand_ink_color);
}

export function SiteSettingsProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [platform, setPlatform] = useState({});

  const load = useCallback(async () => {
    try {
      const [content, plat] = await Promise.all([
        api.get("/marketing/content").catch(() => ({ data: {} })),
        api.get("/public/platform-settings").catch(() => ({ data: {} })),
      ]);
      setSettings(content.data);
      setPlatform(plat.data || {});
      applySiteSettings(content.data);
    } catch {
      setSettings({});
      setPlatform({});
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <SiteSettingsContext.Provider value={{ settings: settings || {}, platform, refresh: load }}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

export function useSiteSettings() {
  return useContext(SiteSettingsContext);
}

/** Normalise WhatsApp number to digits-only for wa.me URLs. */
export function normalizeWa(num) {
  return String(num || "").replace(/[^0-9]/g, "");
}
